import React from 'react'
import { useRouter } from 'expo-router'
import { useMutation } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import type { Id } from '@ppwr/convex/_generated/dataModel'
import { events } from '@/models/analytics/analytics-events'
import { useStore } from '@/models'
import { useOnTransitionToTrue } from '@/hooks/useOnTransitionToTrue'
import { StickyDecisionModal } from '@/screens/shared/StickyDecisionModal'
import { useMyTournamentTable } from './useMyTournamentTable'
import { redirectToLeaderboard } from './mttNavigation'
import { useAsyncMutation } from './lobby/useAsyncMutation'

interface Props {
  tournamentId: Id<'tournaments'>
  userId: string
}

/**
 * Host/co-host "Continue with Bots or End Now?" prompt.
 *
 * Mount once per (tournamentId, userId). Self-gates on the server's
 * `hostBotDecision` field. The server only surfaces that field to the
 * targeted recipient (last-busted host or co-host), so any user seeing
 * it populated IS the right recipient — no client-side admin check.
 *
 * Mounted independently of `TournamentHUD` so that a busted host (whose
 * `myPlayer` row is gone) still receives the prompt. See git history of
 * `TournamentHUD.tsx` for the bug this avoids.
 */
export function TournamentHostBotPrompt({ tournamentId, userId }: Props) {
  const router = useRouter()
  const { setEvent } = useStore().analytics

  const myTable = useMyTournamentTable(tournamentId)
  const resolveHostBotDecision = useMutation(api.tournaments.hostManagement.resolveHostBotDecision)

  // Suppress while a reentry is also pending — let the reentry modal resolve first.
  // (The server resolves reentries before re-checking host-bot eligibility on the
  // next orchestratePostHand cycle, so the two prompts shouldn't truly overlap,
  // but this defensive gate guarantees reentry wins UX-wise if they ever do.)
  const visible = !!myTable?.hostBotDecision && !myTable?.pendingReentry

  useOnTransitionToTrue(visible, () => {
    setEvent(events.mtt.host_bot_prompt_viewed, { tournamentId })
  })

  // Two `useAsyncMutation` calls so each button has its own `pending` flag
  // for the modal's `pendingKey` discriminator. Failures route through the
  // toast queue automatically — previously a failed resolve printed to
  // `console.error` (invisible on native) and left the user staring at a
  // modal that didn't respond to taps.
  const continueWithBots = useAsyncMutation(
    async () => {
      setEvent(events.mtt.continue_with_bots_tapped, { tournamentId })
      await resolveHostBotDecision({ tournamentId, userId, continueWithBots: true })
    },
    {
      errorMessage: 'Could not continue with bots. Please try again.',
      onSuccess: () => redirectToLeaderboard(router, tournamentId)
    }
  )

  const endNow = useAsyncMutation(
    async () => {
      setEvent(events.mtt.end_tournament_now_tapped, { tournamentId })
      await resolveHostBotDecision({ tournamentId, userId, continueWithBots: false })
    },
    {
      errorMessage: 'Could not end tournament. Please try again.',
      onSuccess: () => redirectToLeaderboard(router, tournamentId)
    }
  )

  if (!visible) return null

  const pendingKey: null | 'continue' | 'end' = continueWithBots.pending
    ? 'continue'
    : endNow.pending
      ? 'end'
      : null

  return (
    <StickyDecisionModal
      visible
      title="Continue as Observer?"
      body="You were the last human player. Continue running the tournament with bots only, or end it now."
      primary={{ label: 'Continue with Bots', onPress: () => continueWithBots.run() }}
      secondary={{ label: 'End Tournament Now', onPress: () => endNow.run() }}
      pendingKey={pendingKey}
    />
  )
}
